import type { IconComponent } from '@consta/icons/Icon'
import { IconAdd } from '@consta/icons/IconAdd'
import { Badge } from '@consta/uikit/Badge'
import { Button } from '@consta/uikit/Button'
import type { TableColumn } from '@consta/uikit/Table'
import { Table } from '@consta/uikit/Table'
import { Text } from '@consta/uikit/Text'
import { uploadFilesExtensions } from '@core/core/modules'
import {
  selectActiveProjectName,
  selectSearchActiveCategory,
  selectSearchInCategoryResult,
  useAppSelector,
} from '@core/core/store'
import type { CrmsAttachment, KsuWell } from '@core/core/types'
import { formatterBytes, getImportFileAddedParams, getImportFileAddParam } from '@core/core/utils'
import { useSpa } from '@core/hooks'
import {
  IconActiveFolder,
  IconDocBlank,
  IconGeoObjectWells,
  IconGeoObjSeismic3D,
} from '@core/icons'
import type { FC, ReactElement } from 'react'

import { useCategoryPaging, useScrollPagingControl } from '../../hooks'
import css from './FoundSubEntitiesTable.css'
import type { CommonRowType, FoundSubEntitiesTableProps as Props } from './types'

type GeoBdCellRenderer = (well: KsuWell) => ReactElement | string | undefined
type CrmsCellRenderer = (attachment: CrmsAttachment) => ReactElement | string | undefined

const renderIconText = (Icon: IconComponent, label: string) => {
  return (
    <div className={css.iconCell}>
      <div className={css.iconWrapper}>
        <Icon size="s" />
      </div>
      <Text truncate={true} size="xs" view="primary">
        {label}
      </Text>
    </div>
  )
}

const getCrmsIcon = (attachment: CrmsAttachment) => {
  if (attachment.type === 'folder') {
    return IconActiveFolder
  }
  const extension = `.${attachment.contentFileName.split('.').pop() ?? ''}`
  return uploadFilesExtensions.Survey3D.includes(extension) ? IconGeoObjSeismic3D : IconDocBlank
}

const renderDateCell = (date: string): ReactElement => (
  <Text className={css.tableCell} size="xs" view="primary">
    {getLocalizedDate(date)}
  </Text>
)

function makeCrmsTree(
  attachments: CrmsAttachment[] | undefined,
  actionRenderer: CrmsCellRenderer,
): CommonRowType[] {
  return (
    attachments?.map(attach => ({
      id: attach.id,
      name: renderIconText(getCrmsIcon(attach), attach.contentFileName),
      size: formatterBytes(attach.contentSize),
      action: attach.type === 'file' ? actionRenderer(attach) : undefined,
      rows: attach.type === 'folder' ? makeCrmsTree(attach.files, actionRenderer) : [],
    })) ?? []
  )
}

function makeGeoDbWells(wells: KsuWell[], actionRenderer: GeoBdCellRenderer): CommonRowType[] {
  return wells.map(well => ({
    id: well.ksuUid,
    name: renderIconText(IconGeoObjectWells, well.wellName),
    date: renderDateCell(well.changedAt),
    action: actionRenderer(well),
  }))
}

const getLocalizedDate = (changedAt: string) => {
  try {
    return new Date(changedAt).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  } catch {
    return changedAt
  }
}

const titleCellRender = (text: string) => (
  <Text className={css.titleCell} size="xs" weight="bold" view="primary">
    {text}
  </Text>
)

export const FoundSubEntitiesTable: FC<Props> = ({ addCrmsFiles, addGeoDFiles }) => {
  const activeCategory = useAppSelector(selectSearchActiveCategory)
  const categoryName = activeCategory?.name ?? ''

  const activeResult = useAppSelector(selectSearchInCategoryResult)
  const activeProjectName = useAppSelector(selectActiveProjectName)

  const sendSpaEvent = useSpa()
  const resultSource = activeResult?.source

  const { pagingInfo, pager } = useCategoryPaging()
  const changeRef = useScrollPagingControl(pager, pagingInfo)

  const onLoadGeoBdWell = (well: KsuWell) => {
    addGeoDFiles([well])
    if (activeProjectName) {
      sendSpaEvent(
        'new import_file added',
        getImportFileAddedParams(well.wellName, categoryName, activeProjectName),
      )
    }
  }

  const renderGeoBdActionCell: GeoBdCellRenderer = (well: KsuWell) => {
    if (well.state === 'notstarted') {
      return (
        <Button
          className={css.tableAddButton}
          size="xs"
          view="ghost"
          iconLeft={IconAdd}
          label="В проект"
          data-analytics-id="new import_add file to project"
          data-analytic-params={getImportFileAddParam(
            well.wellName,
            categoryName,
            activeProjectName,
          )}
          onClick={() => {
            onLoadGeoBdWell(well)
          }}
        />
      )
    } else if (well.state === 'loading') {
      return <Badge label="Загружается" size="s" view="stroked" />
    } else if (well.state === 'addedToProject') {
      return <Badge label="В ПРОЕКТЕ" size="s" view="stroked" status="success" />
    }

    return
  }

  const onLoadCrmsFile = (attachment: CrmsAttachment) => {
    addCrmsFiles([attachment])
    if (activeProjectName) {
      sendSpaEvent(
        'new import_file added',
        getImportFileAddedParams(
          attachment.contentFileName,
          categoryName,
          activeProjectName,
          'ЦРМС',
        ),
      )
    }
  }

  const renderCrmsActionCell: CrmsCellRenderer = (attachment: CrmsAttachment) => {
    if (attachment.state === 'notstarted') {
      return (
        <Button
          className={css.tableAddButton}
          size="xs"
          view="ghost"
          iconLeft={IconAdd}
          label="В проект"
          data-analytics-id="new import_add file to project"
          data-analytic-params={getImportFileAddParam(
            attachment.contentFileName,
            categoryName,
            activeProjectName,
          )}
          onClick={() => {
            onLoadCrmsFile(attachment)
          }}
        />
      )
    } else if (attachment.state === 'loading') {
      return <Badge label="Загружается" size="s" view="stroked" />
    } else if (attachment.state === 'addedToProject') {
      return <Badge label="В ПРОЕКТЕ" size="s" view="stroked" status="success" />
    }

    return
  }

  let rows: CommonRowType[]
  if (activeResult?.source === 'crms') {
    rows = makeCrmsTree(activeResult.attachments, renderCrmsActionCell)
  } else if (activeResult?.source === 'geodb') {
    rows = makeGeoDbWells(activeResult.wells, renderGeoBdActionCell)
  } else {
    rows = []
  }

  let columns: (TableColumn<CommonRowType> & { accessor: string })[] = [
    {
      title: titleCellRender('Группа'),
      accessor: 'name',
    },
    {
      title: titleCellRender('Размер'),
      accessor: 'size',
      width: 314,
    },
    {
      title: titleCellRender('Дата'),
      accessor: 'date',
      width: 314,
    },
    {
      title: titleCellRender('Действие'),
      accessor: 'action',
      width: 314,
      align: 'right',
    },
  ]

  const isActiveModeGeoDb = resultSource === 'geodb'
  if (isActiveModeGeoDb) {
    // Пока отсутствуют тип поиска "Геомейт", убираем столбец размер только для Geodb
    // Так как при поиске по "Всем базам данных" activeResult == undefined, но в этом случае результаты по crms могут быть непустыми
    columns = columns.filter(col => col.accessor !== 'size')
  }

  return (
    <Table
      ref={changeRef}
      data-testid="geobdWellsResult"
      className={`${css.table} ${isActiveModeGeoDb ? css.columns3 : css.columns4} `}
      columns={columns}
      rows={rows}
      borderBetweenRows={true}
      size="s"
      verticalAlign="center"
      getCellWrap={() => 'truncate'}
      stickyHeader={true}
      isResizable={true}
    />
  )
}
